// frontend/src/pages/PlanHub.js

import { Link } from "react-router-dom";
import React, { useEffect, useRef, useState, useMemo } from "react";
import BackButton from "../components/BackButton";
import { api } from "../api";

import {
  Bell,
  CalendarBlank,
  CheckCircle,
  Fire,
  Target,
} from "phosphor-react";

// --- stable date utils (avoid TZ shifts) ---
const pad2 = (n) => String(n).padStart(2, "0");
const dateKey = (val) => {
  if (!val) return null;
  if (typeof val === "string") {
    const m = val.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  }
  const dt = new Date(val);
  if (Number.isNaN(dt)) return null;
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
};

const groupByDay = (list = []) => {
  const map = {};
  list.forEach((a) => {
    const key = dateKey(a.timestamp || a.created_at);
    if (!key) return;
    (map[key] ||= []).push(a);
  });
  Object.values(map).forEach((arr) =>
    arr.sort(
      (a, b) =>
        new Date(a.timestamp || a.created_at) -
        new Date(b.timestamp || b.created_at)
    )
  );
  return map;
};

async function getToken() {
  try {
    const { Preferences } = await import("@capacitor/preferences");
    const pref = await Preferences.get({ key: "auth_token" });
    if (pref?.value) return pref.value;
  } catch {}
  return (
    localStorage.getItem("token") ||
    sessionStorage.getItem("token") ||
    null
  );
}
async function getAuthHeaders() {
  const token = await getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function PlanHub() {
    const rootRef = useRef(null);
  
    const [activitiesByDay, setActivitiesByDay] = useState({});
    useEffect(() => {
      (async () => {
        try {
          const headers = await getAuthHeaders();
          const meRes = await api.get("/users/me", { headers });
          const me = meRes?.data;
          if (!me?.id) {
            setActivitiesByDay({});
            return;
          }
          const actsRes = await api.get(`/users/${me.id}/activities`, { headers });
          const acts = Array.isArray(actsRes?.data) ? actsRes.data : [];
          setActivitiesByDay(groupByDay(acts));
        } catch {
          setActivitiesByDay({});
        }
      })();
    }, []);
  
    const weeklyActivityCount = useMemo(() => {
      const now = new Date();

      // Monday at midnight
      const startOfWeek = new Date(now);
      const day = startOfWeek.getDay();
      const daysSinceMonday = day === 0 ? 6 : day - 1;

      startOfWeek.setDate(startOfWeek.getDate() - daysSinceMonday);
      startOfWeek.setHours(0, 0, 0, 0);

      // Next Monday at midnight
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(endOfWeek.getDate() + 7);

      return Object.entries(activitiesByDay || {}).reduce(
        (total, [date, activities]) => {
          const activityDate = new Date(`${date}T00:00:00`);

          const isCurrentWeek =
            activityDate >= startOfWeek && activityDate < endOfWeek;

          if (!isCurrentWeek) return total;

          return total + (Array.isArray(activities) ? activities.length : 0);
        },
        0
      );
    }, [activitiesByDay]);
  
    // Count activity-days in the CURRENT month to control badge visibility
    const monthCount = useMemo(() => {
      const now = new Date();
      const prefix = `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-`;
      return Object.keys(activitiesByDay || {}).filter((k) => k.startsWith(prefix)).length;
    }, [activitiesByDay]);
  
    const [goals, setGoals] = useState([]);
    const [reminders, setReminders] = useState([]);

    useEffect(() => {
      (async () => {
        try {
          const headers = await getAuthHeaders();

          const [goalsResult, remindersResult] = await Promise.allSettled([
            api.get("/goals", { headers }),
            api.get("/reminders", { headers }),
          ]);

          if (goalsResult.status === "fulfilled") {
            const data = goalsResult.value?.data;
            setGoals(Array.isArray(data) ? data : []);
          }

          if (remindersResult.status === "fulfilled") {
            const data = remindersResult.value?.data;
            setReminders(Array.isArray(data) ? data : []);
          }
        } catch {
          setGoals([]);
          setReminders([]);
        }
      })();
    }, []);

    const monthName = new Intl.DateTimeFormat("en-US", {
      month: "long",
    }).format(new Date());

    const isCompleted = (item) =>
      item?.completed === true ||
      item?.is_completed === true ||
      item?.status === "completed" ||
      item?.status === "done";

    const completedGoals = goals.filter(isCompleted).length;
    const completedReminders = reminders.filter(isCompleted).length;

    const goalValue =
      goals.length > 0 ? `${completedGoals} of ${goals.length}` : "";

    const reminderValue =
      reminders.length > 0
        ? `${completedReminders} of ${reminders.length}`
        : "";

    const items = [
      {
        icon: CalendarBlank,
        label: "Monthly Progress",
        value: monthCount > 0 ? `${monthCount} days` : "",
        to: "/plan/monthly",
      },
      {
        icon: Fire,
        label: "Current Streak",
        value:
          weeklyActivityCount > 0
            ? `${weeklyActivityCount} ${
                weeklyActivityCount === 1 ? "day" : "days"
              }`
            : "",
        to: "/plan/weekly",
      },
      {
        icon: CheckCircle,
        label: "Weekly Plan",
        value: "",
        to: "/plan/weekly-plan",
      },
      {
        icon: Target,
        label: `${monthName} Goals`,
        value: goalValue,
        to: "/plan/goals",
      },
      {
        icon: Bell,
        label: "Reminders",
        value: reminderValue,
        to: "/plan/reminders",
      },
    ];
      
    return (
      <div ref={rootRef} className="app-shell bg-blue-50 min-h-[100svh]">
        {/* Fixed Header */}
        <header
          className="sticky top-0 left-0 right-0 z-30 bg-blue-50"
          style={{ paddingTop: "env(safe-area-inset-top)" }}
        >
          <div className="mt-2 px-4 flex items-center">
            <BackButton className="text-blue-600" ariaLabel="Back" />
            <h1 className="flex-1 text-left text-[20px] text-blue-600 dm-font font-black tracking-[-0.06em] leading-[0.9] select-none">Plan</h1>
          </div>
        </header>
  
        {/* Spacer: exact header height + safe area so content starts below it
        <div style={{ height: "calc(48px + env(safe-area-inset-top))" }} /> */}
  
        {/* Content */}
        <main
          className="app-content px-4 pt-1"
          style={{
            paddingBottom: "calc(env(safe-area-inset-bottom) + 72px)",
          }}
        >
          <section className="bg-transparent pl-1 pr-2">

            <div className="space-y-4">
              {items.map(({ icon: Icon, label, value, to }) => (
                <Link
                  key={label}
                  to={to}
                  className="
                    flex items-center justify-between rounded-full bg-white
                    !min-h-0 !min-w-0 px-5 py-2.5 font-semibold
                    transition 
                  "
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <Icon
                      size={17}
                      weight="duotone"
                      className="shrink-0 text-blue-600"
                    />

                    <span className="truncate text-[18px] text-gray-800">
                      {label}
                    </span>
                  </div>

                  {value && (
                    <span className="ml-2 shrink-0 text-sm font-medium text-gray-500">
                      {value}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          </section>

          <div className="app-footer-pad" />
        </main>
      </div>
    );
  }
  